import { ARTIFACT_KINDS } from "../code-architecture-assurance";
import { loadArtifactRowsAsync, storageKeyFor } from "../code-architecture-assurance/artifactUtils";
import {
  buildCrossRepoFunctionalRows,
  buildRepoTraceMap,
  CROSS_REPO_ARTIFACT_LOADS,
  emptyRepoAssuranceArtifacts,
  remapArtifactRows,
} from "../code-architecture-cross-repo/CrossRepoArchitecturePanel";
import {
  CROSS_REPO_ARCHITECTURE_KIND,
  getCbaProjectsInFolderTree,
  getCrossRepoGeneratedMeta,
  normalizeCrossRepoRow,
} from "../code-architecture-cross-repo";
import { getLatestCodeArchitectureHazardRun } from "../code-architecture-hazard-analysis";
import { safetyRemediationStore } from "../safety-remediation";
import {
  XHANDLE_IDB_NAME,
  codeArchitectureMetaKey,
  codeArchitectureRowsKey,
  readCbaRowsFromIndexedDB,
} from "../code-architecture-assurance/codeArchitectureStorage";
import {
  codeArchitectureReviewPackageStartBody,
  codeArchitectureReviewPackagingTarget,
  reviewPackagerRequestHeaders,
} from "./reviewPackagerConfig";
export {
  codeArchitectureReviewPackagingTarget,
  configuredReviewPackagerUrl,
  isHostedCodeArchitectureReviewPackagerConfigured,
} from "./reviewPackagerConfig";

export const CODE_ARCHITECTURE_REVIEW_PACKAGE_SCHEMA_VERSION = 1;
export const CODE_ARCHITECTURE_REVIEW_PACKAGE_TYPE = "code-based-architecture-review-package";
export const REVIEW_ANALYSIS_SECTIONS = {
  HAZARD: "hazard-remediation",
  SOFTWARE: ARTIFACT_KINDS.SOFTWARE,
  SYSTEM: ARTIFACT_KINDS.SYSTEM,
  SUBSYSTEM: ARTIFACT_KINDS.SUBSYSTEM,
  DESIGN: ARTIFACT_KINDS.DESIGN,
  TRACEABILITY: "traceability-matrix",
};
const DIAGRAM_POSITIONS_STORE = "diagram_positions";

function safeJsonParse(raw, fallback = null) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function readLocalStorageJson(key, fallback = null) {
  if (typeof localStorage === "undefined" || !key) return fallback;
  return safeJsonParse(localStorage.getItem(key), fallback);
}

function readLocalStorageValue(key, fallback = null) {
  if (typeof localStorage === "undefined" || !key) return fallback;
  const value = localStorage.getItem(key);
  return value == null ? fallback : value;
}

function openDb(name) {
  if (typeof indexedDB === "undefined" || !name) return Promise.resolve(null);
  return new Promise((resolve) => {
    const request = indexedDB.open(name);
    request.onerror = () => resolve(null);
    request.onsuccess = () => resolve(request.result);
  });
}

async function readIndexedDbRecord(database, storeName, key) {
  const db = await openDb(database);
  if (!db || !storeName || !key || !db.objectStoreNames.contains(storeName)) {
    try { db?.close?.(); } catch {}
    return null;
  }
  return new Promise((resolve) => {
    const tx = db.transaction(storeName, "readonly");
    const request = tx.objectStore(storeName).get(key);
    request.onerror = () => resolve(null);
    request.onsuccess = () => resolve(request.result || null);
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      try { db.close(); } catch {}
      resolve(null);
    };
  });
}

function diagramStorageKey(repo = {}) {
  const repoId = repo.repoId || repo.repoName || repo.id || "repo";
  const branch = repo.branch || "main";
  return `diagram:github:${repoId}:${branch}`;
}

function tableUiStateFor(storageKey) {
  return {
    hiddenColumns: readLocalStorageJson(`${storageKey}:hidden-columns`, []),
    columnWidths: readLocalStorageJson(`${storageKey}:column-widths`, {}),
  };
}

function sanitizeRepo(repo = {}) {
  const { token, accessToken, authToken, password, ...safeRepo } = repo || {};
  return safeRepo;
}

function repoDisplayName(project, repo) {
  return repo?.repoName || repo?.repoId || [repo?.owner, repo?.repo].filter(Boolean).join("/") || project?.name || "Repository";
}

function uniqueStrings(values) {
  return Array.from(new Set(
    values
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  ));
}

function repoIdentityCandidates(repo = {}) {
  return uniqueStrings([
    repo.id,
    repo.repoId,
    repo.repoName,
    [repo.owner, repo.repo].filter(Boolean).join("/"),
  ]);
}

async function loadBestArtifactRows(kind, projectId, repoIds) {
  let bestRows = [];
  for (const repoId of repoIds) {
    const rows = await loadArtifactRowsAsync(kind, projectId, repoId);
    if (Array.isArray(rows) && rows.length > bestRows.length) {
      bestRows = rows;
    }
  }
  return bestRows;
}

async function loadAssuranceArtifacts(projectId, repo, includeMap) {
  const repoIds = repoIdentityCandidates(repo);
  const shouldIncludeArtifactKind = (kind) => (
    !includeMap ||
    includeMap[kind] ||
    includeMap[REVIEW_ANALYSIS_SECTIONS.TRACEABILITY]
  );
  const artifactEntries = await Promise.all(
    [
      ARTIFACT_KINDS.SOFTWARE,
      ARTIFACT_KINDS.SYSTEM,
      ARTIFACT_KINDS.SUBSYSTEM,
      ARTIFACT_KINDS.DESIGN,
    ].map(async (kind) => [
      kind,
      shouldIncludeArtifactKind(kind) ? await loadBestArtifactRows(kind, projectId, repoIds) : [],
    ])
  );
  return Object.fromEntries(artifactEntries);
}

function idSet(values) {
  return new Set(uniqueStrings(values));
}

function recordMatchesScope(record, { projectId, repoIds }) {
  if (!record) return false;
  const recordProjectId = String(record.projectId || "").trim();
  const recordRepoId = String(record.repoId || record.repoName || "").trim();
  if (projectId && recordProjectId && recordProjectId !== String(projectId)) return false;
  if (repoIds.size && recordRepoId && !repoIds.has(recordRepoId)) return false;
  return true;
}

async function loadSafetyRemediationContext({ projectId, repo }) {
  const repoIds = idSet(repoIdentityCandidates(repo));
  const state = await safetyRemediationStore.loadAll();
  const safetyFindings = (state.safetyFindings || []).filter((finding) => (
    recordMatchesScope(finding, { projectId, repoIds })
  ));
  const findingIds = idSet(safetyFindings.map((finding) => finding.id));
  const patchProposals = (state.patchProposals || []).filter((patch) => (
    findingIds.has(patch.safetyFindingId) ||
    recordMatchesScope(patch, { projectId, repoIds })
  ));
  const patchIds = idSet(patchProposals.map((patch) => patch.id));
  const reviewDecisions = (state.reviewDecisions || []).filter((decision) => (
    findingIds.has(decision.targetId) ||
    patchIds.has(decision.targetId) ||
    recordMatchesScope(decision, { projectId, repoIds })
  ));
  const verificationRuns = (state.verificationRuns || []).filter((run) => (
    findingIds.has(run.safetyFindingId) ||
    patchIds.has(run.patchProposalId) ||
    recordMatchesScope(run, { projectId, repoIds })
  ));
  const safetyRemediationEvidence = (state.safetyRemediationEvidence || []).filter((evidence) => (
    findingIds.has(evidence.safetyFindingId) ||
    patchIds.has(evidence.patchProposalId) ||
    recordMatchesScope(evidence, { projectId, repoIds })
  ));
  const summaryArtifacts = (state.summaryArtifacts || []).filter((summary) => (
    findingIds.has(summary.safetyFindingId) ||
    patchIds.has(summary.patchProposalId) ||
    findingIds.has(summary.findingId) ||
    patchIds.has(summary.patchId) ||
    recordMatchesScope(summary, { projectId, repoIds })
  ));

  if (
    !safetyFindings.length &&
    !patchProposals.length &&
    !reviewDecisions.length &&
    !summaryArtifacts.length &&
    !verificationRuns.length &&
    !safetyRemediationEvidence.length
  ) {
    return null;
  }

  return {
    safetyFindings,
    patchProposals,
    reviewDecisions,
    summaryArtifacts,
    verificationRuns,
    safetyRemediationEvidence,
  };
}

async function buildProjectRepoPackage({ project, folder = null, repo, activeRepo = null, cbaRows = null, includedAnalysis = null }) {
  const rowsKey = codeArchitectureRowsKey(project.id, repo.id);
  const rows = repo.id === activeRepo?.id && Array.isArray(cbaRows) && cbaRows.length
    ? cbaRows
    : await readCbaRowsFromIndexedDB(rowsKey);
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const includeMap = includedAnalysis && typeof includedAnalysis === "object" ? includedAnalysis : null;
  const assuranceArtifacts = await loadAssuranceArtifacts(project.id, repo, includeMap);
  const includeSafetyRemediation = !includeMap || includeMap[REVIEW_ANALYSIS_SECTIONS.HAZARD];
  const safetyRemediation = includeSafetyRemediation
    ? await loadSafetyRemediationContext({ projectId: project.id, repo })
    : null;
  const diagramKey = diagramStorageKey(repo);
  const diagramPositions = await readIndexedDbRecord(XHANDLE_IDB_NAME, DIAGRAM_POSITIONS_STORE, diagramKey);
  const repoMetaKey = codeArchitectureMetaKey(project.id, repo.id);
  const repoMeta = readLocalStorageJson(repoMetaKey, null);
  const safeRepo = sanitizeRepo(repo);

  return {
    id: `project:${project.id}:${safeRepo.id}`,
    type: "project",
    label: `${project.name || "Code Architecture"} - ${repoDisplayName(project, safeRepo)}`,
    project: { ...project, repos: (project.repos || []).map(sanitizeRepo) },
    folder,
    repo: safeRepo,
    repoMeta,
    rows,
    diagramPositions: diagramPositions?.value || diagramPositions || null,
    assuranceArtifacts,
    safetyRemediation,
    storage: {
      cbaRows: {
        database: XHANDLE_IDB_NAME,
        store: "copilot_baseline",
        key: rowsKey,
      },
      repoMetaKey,
      diagramPositions: {
        database: XHANDLE_IDB_NAME,
        store: DIAGRAM_POSITIONS_STORE,
        key: diagramKey,
      },
      assuranceArtifacts: Object.fromEntries(
        Object.keys(assuranceArtifacts).map((kind) => [kind, storageKeyFor(kind, project.id, repo.id)])
      ),
    },
    uiState: {
      codeArchitectureFunctionalTable: tableUiStateFor(`code-architecture-functional-table:${project.id}:${repo.id}`),
      diagram: {
        storageKey: diagramKey,
      },
      assuranceTables: Object.fromEntries(
        Object.keys(assuranceArtifacts).map((kind) => [kind, tableUiStateFor(storageKeyFor(kind, project.id, repo.id))])
      ),
    },
  };
}

async function buildCrossRepoPackage({ folder, folders = [], projects = [], includedAnalysis = null }) {
  const folderId = folder?.id || "";
  if (!folderId) return null;
  const rawRows = await loadArtifactRowsAsync(CROSS_REPO_ARCHITECTURE_KIND, folderId, "folder");
  const rows = (Array.isArray(rawRows) ? rawRows : []).map(normalizeCrossRepoRow);

  const folderProjects = getCbaProjectsInFolderTree(projects, folders, folderId);
  const generatedMeta = getCrossRepoGeneratedMeta(folderId);
  if (!rows.length && !generatedMeta?.generatedAt && folderProjects.length < 2) return null;
  const repoFunctionalRows = [];
  const artifacts = emptyRepoAssuranceArtifacts();
  const includeMap = includedAnalysis && typeof includedAnalysis === "object" ? includedAnalysis : null;
  const shouldIncludeArtifactKind = (kind) => (
    !includeMap ||
    includeMap[kind] ||
    includeMap[REVIEW_ANALYSIS_SECTIONS.TRACEABILITY]
  );
  for (const project of folderProjects) {
    for (const repo of project.repos || []) {
      const repoRows = await readCbaRowsFromIndexedDB(codeArchitectureRowsKey(project.id, repo.id));
      const normalizedRepoRows = Array.isArray(repoRows) ? repoRows : [];
      repoFunctionalRows.push({ project, repo, rows: normalizedRepoRows });
      const traceMap = buildRepoTraceMap(project, repo, normalizedRepoRows);
      for (const [artifactKey, kind] of CROSS_REPO_ARTIFACT_LOADS) {
        if (!shouldIncludeArtifactKind(kind)) continue;
        const artifactRows = await loadBestArtifactRows(kind, project.id, repoIdentityCandidates(repo));
        artifacts[artifactKey].push(...remapArtifactRows(artifactRows, kind, traceMap));
      }
    }
  }

  const functionalRows = buildCrossRepoFunctionalRows({ rows, folderProjects, repoFunctionalRows });
  const virtualProject = { id: folderId, name: folder?.name || "Cross-Repo Architecture" };
  const virtualRepo = {
    id: "folder",
    repoId: "folder",
    repoName: `${folder?.name || "Folder"} Cross-Repo`,
    repoUrl: "",
    branch: "folder",
  };
  return {
    id: `cross-repo:${folderId}`,
    type: "cross-repo",
    label: `${folder?.name || "Folder"} Cross-Repo Architecture`,
    project: virtualProject,
    folder,
    repo: virtualRepo,
    repoMeta: virtualRepo,
    rows: functionalRows,
    diagramPositions: null,
    assuranceArtifacts: artifacts,
    rawCrossRepoRows: rows,
    storage: {
      cbaRows: {
        database: XHANDLE_IDB_NAME,
        store: "artifactRows",
        key: storageKeyFor(CROSS_REPO_ARCHITECTURE_KIND, folderId, "folder"),
      },
    },
    uiState: {
      codeArchitectureFunctionalTable: tableUiStateFor(`code-architecture-functional-table:cross-repo:${folderId}`),
      diagram: {
        storageKey: `diagram:github:cross-repo:${folderId}:folder`,
      },
    },
  };
}

function hazardSummaryCount(run) {
  return Array.isArray(run?.generatedSheets?.Summary)
    ? Math.max(0, run.generatedSheets.Summary.length - 1)
    : 0;
}

function hazardRunMatchesRepo(run, repo) {
  if (!run || !repo) return false;
  return (
    run.repoId === repo.id ||
    run.repoId === repo.repoId ||
    run.repoId === repo.repoName
  );
}

async function attachHazardRun(packageEntry, { activeRepo = null, activeHazardRun = null, includedAnalysis = null } = {}) {
  if (!packageEntry) return packageEntry;
  const includeHazard = !includedAnalysis || includedAnalysis[REVIEW_ANALYSIS_SECTIONS.HAZARD];
  if (!includeHazard) return { ...packageEntry, hazardRun: null };
  if (
    packageEntry.type === "project" &&
    activeHazardRun &&
    (
      packageEntry.repo?.id === activeRepo?.id ||
      hazardRunMatchesRepo(activeHazardRun, packageEntry.repo)
    ) &&
    hazardSummaryCount(activeHazardRun) > 0
  ) {
    return { ...packageEntry, hazardRun: activeHazardRun };
  }
  const projectId = packageEntry.project?.id || "";
  const repoId = packageEntry.type === "cross-repo" ? "folder" : packageEntry.repo?.id || "";
  if (!projectId || !repoId) return { ...packageEntry, hazardRun: null };
  const storedRun = await getLatestCodeArchitectureHazardRun({ projectId, repoId });
  return {
    ...packageEntry,
    hazardRun: hazardSummaryCount(storedRun) > 0 ? storedRun : null,
  };
}

function normalizeReviewTargets({ project, folder, repo, repos, cbaRows, reviewTargets }) {
  if (Array.isArray(reviewTargets) && reviewTargets.length) return reviewTargets;
  if (!project?.id) return [];
  return [{
    type: "project",
    project,
    folder,
    repos: (Array.isArray(repos) && repos.length ? repos : [repo]).filter((entry) => entry?.id),
    activeRepo: repo,
    cbaRows,
  }];
}

function downloadJson(value, filename) {
  const blob = new Blob([JSON.stringify(value, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  downloadBlob(blob, filename);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function collectCodeArchitectureReviewPackage({
  project,
  folder = null,
  repo = null,
  repos = null,
  cbaRows = null,
  hazardRun = null,
  reviewTargets = null,
  appDisplayName = "",
  includedAnalysis = null,
  reviewItems = [],
  uiState = {},
} = {}) {
  const targets = normalizeReviewTargets({ project, folder, repo, repos, cbaRows, reviewTargets });
  if (!targets.length) throw new Error("Select at least one Code-Based Architecture project before exporting.");

  const builtPackages = [];
  for (const target of targets) {
    if (target?.type === "cross-repo") {
      const crossRepoPackage = await buildCrossRepoPackage({
        folder: target.folder,
        folders: target.folders || [],
        projects: target.projects || [],
        includedAnalysis,
      });
      if (crossRepoPackage) builtPackages.push(crossRepoPackage);
      continue;
    }

    const targetProject = target?.project;
    const targetRepos = (Array.isArray(target?.repos) && target.repos.length
      ? target.repos
      : targetProject?.repos || []
    ).filter((entry) => entry?.id);
    for (const targetRepo of targetRepos) {
      const packageEntry = await buildProjectRepoPackage({
        project: targetProject,
        folder: target.folder || null,
        repo: targetRepo,
        activeRepo: target.activeRepo || repo,
        cbaRows: target.cbaRows,
        includedAnalysis,
      });
      if (packageEntry) builtPackages.push(packageEntry);
    }
  }

  const repoPackages = (await Promise.all(builtPackages.map((entry) => (
    attachHazardRun(entry, {
      activeRepo: repo,
      activeHazardRun: hazardRun,
      includedAnalysis,
    })
  )))).filter(Boolean);

  if (!repoPackages.length) {
    throw new Error("Analyze at least one selected Code-Based Architecture project before exporting a review package.");
  }

  const activeRepoPackage = repoPackages.find((entry) => entry.repo?.id === repo?.id) || repoPackages[0];
  const safeRepo = activeRepoPackage.repo;
  const repoMeta = activeRepoPackage.repoMeta;
  const assuranceArtifacts = activeRepoPackage.assuranceArtifacts;
  const safetyRemediation = activeRepoPackage.safetyRemediation || null;
  const hazardRunForActiveRepo = activeRepoPackage.hazardRun || null;
  const activeProject = activeRepoPackage.project || project || {};

  return {
    schemaVersion: CODE_ARCHITECTURE_REVIEW_PACKAGE_SCHEMA_VERSION,
    artifactType: CODE_ARCHITECTURE_REVIEW_PACKAGE_TYPE,
    exportedAt: new Date().toISOString(),
    reviewMode: true,
    appDisplayName: String(appDisplayName || "").trim() || "xHandle Code Architecture Review",
    project: {
      ...activeProject,
      repos: (activeProject.repos || []).map(sanitizeRepo),
    },
    folder: activeRepoPackage.folder || folder,
    activeRepo: safeRepo,
    repoMeta,
    storage: {
      ...activeRepoPackage.storage,
      repositories: Object.fromEntries(repoPackages.map((entry) => [entry.id, entry.storage])),
    },
    data: {
      cbaRows: activeRepoPackage.rows,
      diagramPositions: activeRepoPackage.diagramPositions,
      assuranceArtifacts,
      safetyRemediation,
      hazardRun: hazardRunForActiveRepo,
      reviewItems: Array.isArray(reviewItems) ? reviewItems : [],
      repositories: repoPackages.map((entry) => ({
        id: entry.id,
        type: entry.type,
        label: entry.label,
        project: entry.project,
        folder: entry.folder || null,
        repo: entry.repo,
        repoMeta: entry.repoMeta,
        cbaRows: entry.rows,
        diagramPositions: entry.diagramPositions,
        assuranceArtifacts: entry.assuranceArtifacts,
        safetyRemediation: entry.safetyRemediation || null,
        hazardRun: entry.hazardRun || null,
      })),
    },
    uiState: {
      activeWorkspaceTab: uiState.activeWorkspaceTab || "architecture",
      hazardRemediationTab: uiState.hazardRemediationTab || "hazard-analysis",
      includedAnalysis: includedAnalysis || null,
      ...activeRepoPackage.uiState,
      hazardSummaryTable: tableUiStateFor(`code-architecture-hazard-summary:${safeRepo.repoId || safeRepo.repoName || "repo"}:${hazardRunForActiveRepo?.id || "latest"}`),
      repositories: Object.fromEntries(repoPackages.map((entry) => [entry.id, entry.uiState])),
      sidebar: {
        codeArchitectureProjectsOpen: readLocalStorageValue("xhandle.sidebarCodeArchitectureProjectsOpen", "true"),
        codeArchitectureFoldersOpen: readLocalStorageJson("xhandle.sidebarCodeArchitectureFoldersOpen", {}),
      },
      ...uiState,
    },
  };
}

function reviewPackageFilenameBase(reviewPackage) {
  const baseName = reviewPackage?.appDisplayName || [
    reviewPackage?.project?.name || "code-architecture",
    reviewPackage?.activeRepo?.repoName || reviewPackage?.activeRepo?.repoId || "repo",
  ].filter(Boolean).join("-");
  return String(baseName)
    .replace(/[^a-z0-9._-]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "code-architecture-review";
}

export function downloadCodeArchitectureReviewPackage(reviewPackage) {
  downloadJson(reviewPackage, `${reviewPackageFilenameBase(reviewPackage)}-review-package.json`);
}

function downloadUrl(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function isAbsoluteHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || ""));
}

function resolveReviewAppDownloadUrl(targetBackendUrl, job = {}) {
  const rawUrl = job.artifactUrl || job.downloadUrl;
  if (!rawUrl) return "";
  if (isAbsoluteHttpUrl(rawUrl)) return rawUrl;
  return `${targetBackendUrl}${String(rawUrl).startsWith("/") ? "" : "/"}${rawUrl}`;
}

function packagingBackendUrl(backendUrl) {
  return codeArchitectureReviewPackagingTarget(backendUrl).url;
}

export async function chooseCodeArchitectureReviewDestination({ backendUrl } = {}) {
  const targetBackendUrl = packagingBackendUrl(backendUrl);
  let response;
  try {
    response = await fetch(`${targetBackendUrl}/api/code-architecture-review/package/choose-destination`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
  } catch (error) {
    throw new Error(
      `Could not open the local folder picker at ${targetBackendUrl}. Start the local xHandle backend, then try again. ${error?.message || ""}`.trim()
    );
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {}

  if (!response.ok) {
    throw new Error(payload?.error || `Could not open folder picker (HTTP ${response.status}).`);
  }
  return payload || { cancelled: true };
}

export async function downloadCodeArchitectureReviewApp(
  reviewPackage,
  { backendUrl, onProgress, reviewAppTarget = "mac", destinationDirectory = "" } = {}
) {
  const packagingTarget = codeArchitectureReviewPackagingTarget(backendUrl);
  const targetBackendUrl = packagingTarget.url;
  const requestBody = codeArchitectureReviewPackageStartBody({
    reviewPackage,
    reviewAppTarget,
    destinationDirectory,
    packagingMode: packagingTarget.mode,
  });
  let response;
  try {
    response = await fetch(`${targetBackendUrl}/api/code-architecture-review/package/start`, {
      method: "POST",
      headers: reviewPackagerRequestHeaders(),
      body: JSON.stringify(requestBody),
    });
  } catch (error) {
    throw new Error(
      packagingTarget.mode === "hosted"
        ? `Could not reach the hosted review app packager at ${targetBackendUrl}. ${error?.message || ""}`.trim()
        : `Could not reach the local review app packager at ${targetBackendUrl}. Start the local xHandle backend, then try again. ${error?.message || ""}`.trim()
    );
  }

  if (!response.ok) {
    let message = `Failed to generate review app (HTTP ${response.status}).`;
    try {
      const payload = await response.json();
      if (payload?.error) message = payload.error;
    } catch {}
    throw new Error(message);
  }

  const startedJob = await response.json();
  return downloadCodeArchitectureReviewAppJob(startedJob, { backendUrl: targetBackendUrl, onProgress });
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function downloadCodeArchitectureReviewAppJob(startedJob, { backendUrl, onProgress } = {}) {
  const targetBackendUrl = packagingBackendUrl(backendUrl);
  let job = startedJob;
  onProgress?.(job);

  while (job && !job.ready && job.status !== "failed") {
    await wait(1500);
    const statusResponse = await fetch(`${targetBackendUrl}/api/code-architecture-review/package/${encodeURIComponent(job.id)}/status`, {
      headers: reviewPackagerRequestHeaders(),
    });
    if (!statusResponse.ok) {
      throw new Error(`Failed to read review app build status (HTTP ${statusResponse.status}).`);
    }
    job = await statusResponse.json();
    onProgress?.(job);
  }

  if (!job || job.status === "failed") {
    const detail = job?.error || job?.logTail || "Review app package build failed.";
    throw new Error(detail);
  }

  const finalDownloadUrl = resolveReviewAppDownloadUrl(targetBackendUrl, job);
  if (!finalDownloadUrl) {
    throw new Error("Review app package is ready, but no download URL was returned.");
  }
  downloadUrl(finalDownloadUrl, job.downloadName || "code-architecture-review-app.zip");
  return job;
}
